/**
 * Игровое поле краша.
 *
 * Компонент перерисовывает canvas на каждом кадре, но сам не перерендеривается:
 * данные берутся из хранилища через getState внутри цикла анимации. Если бы
 * множитель хранился в состоянии React, всё дерево обновлялось бы 60 раз в
 * секунду и на телефоне это было бы заметно.
 */

import { useEffect, useRef } from "react";

import { CrashPhase, multiplierAt, timeToReach } from "@koshkodrop/shared";

import { useCrash } from "../../store/crash";
import { CrashScene, type SceneState } from "./scene";

export function CrashCanvas() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const scene = new CrashScene(canvas);
    let frame = 0;
    /** Момент краша по нашим часам: нужен для анимации падения. */
    let crashedAtLocal: number | null = null;
    let lastPhase: CrashPhase | null = null;

    const observer = new ResizeObserver(() => scene.resize());
    observer.observe(canvas);

    const render = (time: number) => {
      const store = useCrash.getState();
      const round = store.round;
      const now = store.serverNow();

      if (round) {
        if (round.phase !== lastPhase) {
          if (round.phase === CrashPhase.Crashed) crashedAtLocal = performance.now();
          if (round.phase === CrashPhase.Betting) crashedAtLocal = null;
          lastPhase = round.phase;
        }

        const elapsedMs =
          round.startedAt === null ? 0 : Math.max(0, now - round.startedAt);

        const state: SceneState = {
          phase: round.phase,
          // После краша кривая замирает на своей последней точке
          elapsedMs:
            round.phase === CrashPhase.Crashed && round.crashPoint !== null
              ? timeToReach(round.crashPoint)
              : elapsedMs,
          multiplier: multiplierAt(elapsedMs),
          crashPoint: round.crashPoint,
          sinceCrashMs: crashedAtLocal === null ? 0 : performance.now() - crashedAtLocal,
          countdownMs: Math.max(0, round.runningStartsAt - now),
          cashedOutAt:
            store.myBet?.status === "cashed_out" ? (store.myBet.cashedOutAt ?? null) : null,
        };

        scene.draw(state, time);
      } else {
        scene.draw(
          {
            phase: CrashPhase.Betting,
            elapsedMs: 0,
            multiplier: 100,
            crashPoint: null,
            sinceCrashMs: 0,
            countdownMs: 0,
            cashedOutAt: null,
          },
          time,
        );
      }

      frame = requestAnimationFrame(render);
    };

    frame = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  return (
    <div className="relative w-full overflow-hidden rounded-3xl border border-white/5 bg-night-900">
      <canvas ref={canvasRef} className="block h-[46vh] max-h-[380px] min-h-[240px] w-full" />
    </div>
  );
}
